import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { MessageCircle, ClipboardCheck, CheckCircle2, Tag } from "lucide-react";
import { motion } from "motion/react";
import useCartStore, { cartSubtotal } from "../store/cartStore";
import { validateCoupon, submitOrderRequest } from "../features/checkout/api";
import { formatPrice } from "../lib/format";
import { buildWhatsAppLink } from "../lib/whatsapp";
import { couponDiscount } from "../lib/orderMath";
import SCSpinner from "../components/loaders/SCSpinner";
import BackButton from "../components/ui/BackButton";
import useSEO from "../hooks/useSEO";

const contactSchema = z.object({
  name: z.string().min(2, "Enter your full name"),
  phone: z.string().min(7, "Enter a valid phone number"),
  address: z.string().optional(),
  notes: z.string().optional(),
});

export default function Checkout() {
  useSEO({ title: "Checkout", noindex: true });
  const items = useCartStore((s) => s.items);
  const clearCart = useCartStore((s) => s.clear);
  const subtotal = cartSubtotal(items);

  const [submittingChannel, setSubmittingChannel] = useState(null);
  const [confirmedChannel, setConfirmedChannel] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const discount = couponDiscount(appliedCoupon, subtotal);
  const total = subtotal - discount;

  async function handleApplyCoupon() {
    if (!couponInput.trim()) return;
    setCheckingCoupon(true);
    try {
      const coupon = await validateCoupon(couponInput.trim());
      setAppliedCoupon(coupon);
      setCouponError("");
    } catch {
      setAppliedCoupon(null);
      setCouponError("That code isn't valid.");
    } finally {
      setCheckingCoupon(false);
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(contactSchema) });

  if (items.length === 0 && !confirmedChannel) {
    return <Navigate to="/cart" replace />;
  }

  // Whichever channel the customer picks, the order lands in the admin
  // panel with the same shape (customer info + a real items list with
  // cost snapshotted per line). That's what lets "record the WhatsApp
  // sale" and "record the admin-request sale" be the same Confirm Sale
  // flow in Admin > Order Requests, instead of WhatsApp orders being
  // invisible to the system entirely.
  function buildOrderRecord(contact, channel) {
    return {
      customerName: contact.name,
      customerPhone: contact.phone,
      deliveryAddress: contact.address || null,
      notes: contact.notes || null,
      channel,
      couponCode: appliedCoupon?.code || null,
      discount,
      items: items.map((i) => ({
        productId: i.productId,
        name: i.name,
        options: i.options,
        price: i.price,
        costPrice: i.costPrice,
        quantity: i.quantity,
      })),
    };
  }

  async function onWhatsApp(contact) {
    setSubmittingChannel("whatsapp");
    try {
      await submitOrderRequest(buildOrderRecord(contact, "whatsapp"));
      const link = buildWhatsAppLink({ contact, items, subtotal, coupon: appliedCoupon, discount });
      window.open(link, "_blank", "noopener,noreferrer");
      clearCart();
      setConfirmedChannel("whatsapp");
    } catch {
      toast.error("Couldn't send your order — check your connection and try again.");
    } finally {
      setSubmittingChannel(null);
    }
  }

  async function onAdminRequest(contact) {
    setSubmittingChannel("admin");
    try {
      await submitOrderRequest(buildOrderRecord(contact, "admin_request"));
      clearCart();
      setConfirmedChannel("admin");
    } catch {
      toast.error("Couldn't send your request — check your connection and try again.");
    } finally {
      setSubmittingChannel(null);
    }
  }

  if (confirmedChannel) {
    return <ConfirmationScreen channel={confirmedChannel} />;
  }

  return (
    <div className="px-4 py-6 pb-10 lg:mx-auto lg:max-w-xl">
      <BackButton fallback="/cart" fallbackLabel="Cart" className="mb-3" />
      <h1 className="mb-1 font-serif text-2xl text-ink">Checkout</h1>
      <p className="mb-5 text-sm text-ink-light">
        We don't process payments online — choose how you'd like to place this order and we'll
        take it from there.
      </p>

      <div className="mb-6 rounded-2xl bg-surface p-4 shadow-card">
        {items.map((item) => (
          <div key={item.key} className="flex items-center justify-between py-1 text-sm">
            <span className="text-ink-light">
              {item.name}
              {item.options?.length > 0 && (
                <span className="text-mono/40"> ({item.options.map((o) => o.value).join(", ")})</span>
              )}{" "}
              x{item.quantity}
            </span>
            <span className="text-ink">{formatPrice(item.price * item.quantity)}</span>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-between border-t border-mono/5 pt-2 text-sm">
          <span className="text-ink-light">Subtotal</span>
          <span className="text-ink">{formatPrice(subtotal)}</span>
        </div>
        {appliedCoupon && (
          <div className="flex items-center justify-between text-sm text-emerald-600 dark:text-emerald-400">
            <span>Discount ({appliedCoupon.code})</span>
            <span>-{formatPrice(discount)}</span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-between border-t border-mono/5 pt-2 text-sm font-medium text-ink">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-2">
        <div className="relative flex-1">
          <Tag size={14} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-light" />
          <input
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            placeholder="Coupon code"
            className="checkout-input pl-9"
          />
        </div>
        <button
          type="button"
          onClick={handleApplyCoupon}
          disabled={checkingCoupon}
          className="rounded-xl border border-mono/10 px-4 py-2.5 text-sm text-ink-light hover:text-ink disabled:opacity-60"
        >
          {checkingCoupon ? "…" : "Apply"}
        </button>
      </div>
      {couponError && <p className="-mt-4 mb-6 text-xs text-red-500">{couponError}</p>}

      <form className="space-y-4">
        <Field label="Full name" error={errors.name?.message}>
          <input {...register("name")} className="checkout-input" placeholder="Jane Doe" />
        </Field>
        <Field label="Phone number" error={errors.phone?.message}>
          <input {...register("phone")} className="checkout-input" placeholder="+1 555 000 0000" />
        </Field>
        <Field label="Delivery address (optional)">
          <textarea {...register("address")} rows={2} className="checkout-input resize-none" />
        </Field>
        <Field label="Notes (optional)">
          <textarea {...register("notes")} rows={2} className="checkout-input resize-none" />
        </Field>

        <div className="space-y-3 pt-2">
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            disabled={!!submittingChannel}
            onClick={handleSubmit(onWhatsApp)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] py-3.5 text-sm font-medium tracking-wide text-white shadow-floating disabled:opacity-60"
          >
            {submittingChannel === "whatsapp" ? (
              <SCSpinner size={18} showMark={false} />
            ) : (
              <MessageCircle size={17} />
            )}
            Order via WhatsApp
          </motion.button>

          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            disabled={!!submittingChannel}
            onClick={handleSubmit(onAdminRequest)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#2b2b2d] py-3.5 text-sm tracking-wide text-white shadow-floating disabled:opacity-60 dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold"
          >
            {submittingChannel === "admin" ? (
              <SCSpinner size={18} showMark={false} />
            ) : (
              <ClipboardCheck size={17} />
            )}
            Send Request — We'll Contact You
          </motion.button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-light">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-500">{error}</span>}
    </label>
  );
}

function ConfirmationScreen({ channel }) {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <CheckCircle2 size={44} className="text-gold-500" />
      </motion.div>
      <h1 className="mt-4 font-serif text-2xl text-ink">Request sent</h1>
      <p className="mt-2 max-w-xs text-sm text-ink-light">
        {channel === "whatsapp"
          ? "We opened WhatsApp with your order pre-filled — send the message and we'll take it from there."
          : "Our team has your order request and will contact you shortly to confirm details and delivery."}
      </p>
      <Link to="/" className="mt-8 rounded-2xl bg-[#2b2b2d] px-8 py-3 text-xs tracking-wide text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold">
        BACK TO HOME
      </Link>
    </div>
  );
}
